export interface Country {
  readonly code: string
  readonly states: string[]
}

export interface Language {
  readonly code: string
  readonly nativeName: string
}

interface FrontendConfiguration {
  readonly shortCommitId: string
  readonly buildTime: Date
  readonly name: string
  readonly address: string
  readonly zipCode: string
  readonly city: string
  readonly country: string
  readonly director: string
  readonly emailAddress: string
  readonly telephoneNumber: string
  readonly registerCourt: string
  readonly registerNumber: string
  readonly vatId: string
  readonly domain: string
  readonly brandName: string
  readonly countries: Country[]
  readonly languages: Language[]
  readonly metaPixelId: string
  readonly ipAddressCountry: string
}

export default function () {
  const { $api } = useNuxtApp()

  const config = useState<FrontendConfiguration | null>('frontendConfiguration', () => null)

  async function getConfiguration(): Promise<FrontendConfiguration> {
    return cloneObject(
      await $api('/api/frontend-configuration'),
    )
  }

  const { t, locale } = useI18n()

  const countryOptions = computed(() => {
    return [
      ...config.value!.countries
        .filter(k => availableCountries.indexOf(k.code as CountryType) > -1)
        .map(k => ({
          value: k.code,
          label: t(`country.${k.code}`),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, locale.value)),
    ]
  })

  const allCountryOptions = computed(() => {
    return [
      ...config.value!.countries
        .map(k => ({
          value: k.code,
          label: t(`country.${k.code}`),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, locale.value)),
    ]
  })

  function getStateLabelByCountry(country: string) {
    const states = config.value!.countries.find(i => i.code === country)?.states || []
    if (states.length) {
      return t(`stateType.${country}`)
    }

    return (t('Region'))
  }

  function getStateOptionsByCountry(country: string) {
    const states = config.value!.countries.find(i => i.code === country)?.states || []

    const stateOptions = states.map(i => ({
      value: i,
      label: i === '' ? '' : t(`state.${country}-${i}`),
    }))

    stateOptions.sort((a, b) =>
      (a.label as string).localeCompare(b.label as string, locale.value),
    )

    return stateOptions
  }

  const languageOptions = computed(() => {
    return [
      ...config.value!.languages
        .map(k => ({
          value: k.code,
          label: t(`language.${k.code}`),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, locale.value)),
    ]
  })

  async function init() {
    config.value = await getConfiguration()
  }

  return {
    config, init, countryOptions, allCountryOptions, getStateLabelByCountry, getStateOptionsByCountry, languageOptions,
  }
}
